import { useTranslate } from '@im/react-localization';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import React, { useLayoutEffect, useRef, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import { DragAndDrop } from '@im/ui-lib/Drag&Drop';
import { formatFileSize } from 'src/util';
import UploadDetails from './UploadDetails';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

const MAX_FILE_SIZE = 10 * 1024 * 1024;

export default function UploadDiscountDialog(props: any) {
  const translate = useTranslate();
  const [openUploadDialog, setOpenUploadDialog] = useState(true);
  const [addMoreFiles, setAddMoreFiles] = useState(false);
  const [isValidated, setIsValidated] = useState(false);
  const [fileList, setFileList] = useState<File[]>([]);
  const scrollRef = useRef<HTMLDivElement | null>();
  const [validation, setValidation] = useState<{ msg: string; type: string }>({
    msg: '',
    type: '',
  });
  const [hasUploadSuccess, setHasUploadSuccess] = useState(false);
  const handleUploadDialogClose = () => {
    setOpenUploadDialog(false);
    props?.handleUploadCloseDialog();
    if (hasUploadSuccess && props.onRefresh) {
      props.onRefresh();
    }
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleUploadFile(e.target.files);
    }
    e.target.value = '';
  };
  const handleUploadFile = (files: FileList | null) => {
    if (files && files[0].size > MAX_FILE_SIZE) {
      setValidation({
        msg: translate('Common.ValidationMaxFileSize', {
          size: formatFileSize(MAX_FILE_SIZE),
        }),
        type: 'error',
      });
      setIsValidated(true);
    } else if (files && !files?.[0].name.endsWith('.xlsx')) {
      setValidation({
        msg: translate('Common.ValidationFileType', {
          type: 'Excel (.xlsx)',
        }),
        type: 'error',
      });
      setIsValidated(true);
    } else if (files?.length) {
      setFileList((file) => [...file, files[0]]);
      setAddMoreFiles(true);
      setIsValidated(true);
      setValidation({
        msg: '',
        type: '',
      });
    }
  };
  useLayoutEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [fileList.length]);
  return (
    <Box>
      <Dialog
        data-testid="gds-vp-upload-Dialog"
        onClose={handleUploadDialogClose}
        open={openUploadDialog}
      >
        <DialogTitle>
          {translate('VendorDeals.ActionUploadTitle')}
          <IconButton
            data-testid="gds-vp-uploaddialog-closeicon"
            onClick={handleUploadDialogClose}
            size="small"
            sx={{ alignSelf: 'center' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent ref={scrollRef}>
          <DialogContentText
            component={'div'}
            mb={1.5}
            sx={(theme) => ({
              fontSize: theme.typography.body2.fontSize,
              lineHeight: theme.typography.body2.lineHeight,
            })}
          >
            {translate('VendorDeals.ActionUploadMessage')}
          </DialogContentText>
          <Box display={isValidated ? 'none' : 'initial'}>
            <DragAndDrop
              accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              data-testid="gds-vp-upload-discount"
              deletedFiles={() => undefined}
              label={`.xlsx (max. ${
                formatFileSize(MAX_FILE_SIZE).split('.')[0]
              }MB)`}
              maxFileSize={MAX_FILE_SIZE}
              name="gds-vp-upload-discount"
              onClose={() => setAddMoreFiles(true)}
              onUpload={handleUploadFile}
            />
          </Box>
          {isValidated && (
            <Stack
              direction="column"
              mt={2}
              spacing={1.5}
              sx={(theme) => ({
                border: `1px dashed ${theme.palette.divider}`,
                borderRadius: `${theme.shape.borderRadius}px`,
                minWidth: '500px',
                padding: `${theme.spacing(2.5)} calc(${theme.spacing()} * 4)`,
              })}
            >
              {fileList.map((file) => (
                <UploadDetails
                  file={file}
                  handleChange={handleChange}
                  key={String(file?.name)}
                  onUploadSuccess={setHasUploadSuccess}
                  rowData={props?.rowData}
                  vendorId={props?.vendorId}
                  vendorName={props?.vendorName}
                />
              ))}
              {validation.type === 'error' && (
                <Stack
                  direction="row"
                  spacing={2}
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  <Typography
                    sx={(theme) => ({
                      color: `${theme.palette.error[700]} !important`,
                      fontSize: theme.typography.body2.fontSize,
                      lineHeight: theme.typography.body2.lineHeight,
                    })}
                  >
                    {validation.msg}
                  </Typography>
                  <Button
                    component="label"
                    data-testid="gds-vp-ta-button"
                    sx={{
                      background: 'transparent !important',
                      boxShadow: 'none !important',
                      width: '65px',
                    }}
                    variant="newText"
                  >
                    {translate('Common.TryAgain')}
                    <input
                      accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                      data-testid="gds-vp-discountUpload"
                      onChange={handleChange}
                      type="file"
                    />
                  </Button>
                </Stack>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Stack direction="row" spacing={1.25}>
            <Button
              data-testid="gds-vp-ddt2-button"
              href="/assets/DiscountTemplate.xlsx"
              variant="newText"
            >
              {translate('VendorProgram.DownloadDiscountTemplate')}
            </Button>
            {addMoreFiles && validation.type === '' ? (
              <Button
                component="label"
                data-testid="gds-vp-amf-button"
                sx={{ width: '115px' }}
                variant="newOutlined"
              >
                {translate('Common.AddMoreFiles')}
                <input
                  accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  data-testid="gds-vp-discountUpload"
                  onChange={handleChange}
                  type="file"
                />
              </Button>
            ) : null}
          </Stack>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
